/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AbsoluteTime,
  Amounts,
  Paytos,
  Result,
  TalerError,
} from "@gnu-taler/taler-util";
import {
  revalidateTransactions,
  useTransactions,
} from "../../hooks/account.js";
import { Props, State, Transaction } from "./index.js";
import { useTranslationContext } from "@gnu-taler/web-util/browser";

export function useComponentState({
  account,
  variant = "history",
  title,
  routeFullHistory,
  routeBack,
  anonymous,
}: Props): State {
  const result = useTransactions(
    account,
    anonymous,
    variant === "recent" ? 5 : 20,
  );
  const { i18n } = useTranslationContext();
  if (!result) {
    return {
      status: "loading",
      error: undefined,
    };
  }
  if (result.tag === "error") {
    const failure = result.error;
    if (failure instanceof TalerError) {
      return {
        status: "loading-error",
        error: failure,
        title: i18n.str`Failed to load transactions.`,
        onRetry: () => void revalidateTransactions(),
      };
    }
    return {
      status: "failed",
      error: failure,
      title: i18n.str`Failed to load transactions.`,
      onRetry: () => void revalidateTransactions(),
    };
  }

  const page = result.value;
  const transactions = page.items
    .map((tx) => {
      const negative = tx.direction === "debit";
      const cp = Paytos.fromString(
        negative ? tx.creditor_payto_uri : tx.debtor_payto_uri,
      );
      const counterpart = Result.orUndefined(cp)?.displayName;

      const when = AbsoluteTime.fromProtocolTimestamp(tx.date);
      const amount = Amounts.parse(tx.amount);
      const subject = tx.subject;
      return {
        id: tx.row_id,
        negative,
        counterpart: counterpart ?? i18n.str`Unknown account`,
        when,
        amount,
        subject,
      };
    })
    .filter((x): x is Transaction => x !== undefined);

  return {
    status: "ready",
    error: undefined,
    variant,
    title:
      title ??
      (variant === "recent"
        ? i18n.str`Recent activity`
        : i18n.str`Transactions`),
    routeFullHistory,
    routeBack,
    transactions,
    onGoNext: page.loadNext,
    onGoStart: page.loadFirst,
    onGoPrevious: page.loadPrev,
  };
}
